/**
 * Gate logic, topic locks, and admin override.
 * Admin override is always the first check; subscription check is centralized here.
 */

import { subscriptionService } from './subscriptionService';

const STORAGE_ADMIN_OVERRIDE = 'ADMIN_OVERRIDE';
const STORAGE_SUBSCRIPTION_STATUS = 'SUBSCRIPTION_STATUS';

export function setAdminOverride(enabled: boolean): void {
  try {
    localStorage.setItem(STORAGE_ADMIN_OVERRIDE, enabled ? 'true' : 'false');
  } catch {
    // ignore
  }
}

/** First 3 topic IDs – always free. Order matches CBR 2026 Tier 3 intro topics. */
export const FREE_TOPICS: readonly string[] = [
  'traffic-lights-signals',
  'roundabout-rules',
  'priority-rules'
];

/**
 * Admin override: skip all gates and locks, full access.
 * Must be the FIRST check in every gate function.
 */
export function isAdminOverride(): boolean {
  try {
    return localStorage.getItem(STORAGE_ADMIN_OVERRIDE) === 'true';
  } catch {
    return false;
  }
}

/**
 * Single subscription check. Use everywhere subscription is checked.
 * Order: (1) admin override, (2) subscriptionService (tier from localStorage),
 * (3) localStorage SUBSCRIPTION_STATUS === 'active', (4) false.
 */
export function isUserSubscribed(): boolean {
  if (isAdminOverride()) return true;
  if (subscriptionService.hasActiveSubscription()) return true;
  try {
    if (localStorage.getItem(STORAGE_SUBSCRIPTION_STATUS) === 'active') return true;
  } catch {
    // ignore
  }
  return false;
}

/**
 * True if this topic is locked for the current user (free user, topic not in FREE_TOPICS).
 */
export function isTopicLocked(topicId: string): boolean {
  if (isAdminOverride()) return false;
  if (isUserSubscribed()) return false;
  return !FREE_TOPICS.includes(topicId);
}
